import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { toast } from "sonner";

// Reports & Feedback (COP-UX-164…166 + RC49-RPT-001…003): read-only dealer tracking surface for
// every Copilot report. Triage is Zelligio-Admin-only (V46-COR-011) — nothing here mutates.
const STATES = ["New", "Reviewing", "Resolved", "Dismissed"];
const STATE_STYLE = {
  New: "bg-orange-50 text-orange-700 border-orange-200",
  Reviewing: "bg-sand text-warmgrey border-border",
  Resolved: "bg-green-50 text-green-700 border-green-200",
  Dismissed: "bg-limestone/40 text-warmgrey border-border",
};
const STATE_MEANING = {
  New: "Received — Zelligio has not started review yet.",
  Reviewing: "Zelligio is actively reviewing this.",
  Resolved: "Zelligio resolved this; see the resolution note.",
  Dismissed: "Closed without change; see the latest update for the reason.",
};
const summarize = (t) => (t || "").trim().replace(/\s+/g, " ").slice(0, 110) + ((t || "").trim().length > 110 ? "…" : "");
const latestUpdate = (r) => {
  const h = (r.history || []).filter((x) => x.action !== "submitted");
  const last = h[h.length - 1];
  if (r.resolution) return { at: r.resolution.at, text: r.resolution.note ? `Resolved — ${r.resolution.note}` : "Resolved" };
  if (last) return { at: last.at, text: `${last.action}${last.note ? ` — ${last.note}` : ""}` };
  return { at: r.created_at, text: "Submitted — awaiting Zelligio review" };
};

export default function DealerReports() {
  const [reports, setReports] = useState(null);
  const [filter, setFilter] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/reports", { params: filter ? { status: filter } : {} });
      setReports(r.data.reports || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reports could not be loaded");
      setReports([]);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="dealer-reports-page">
      <div className="mb-6">
        <h1 className="font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Reports &amp; Feedback</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-warmgrey" data-testid="reports-value-copy">
          Problems and feedback you submit with <span className="font-medium text-charcoal">Report</span> in ZELI are tracked here.
          Use this page to see what Zelligio is reviewing, what has been resolved and the full history of each report — so you
          never need to re-send the same issue through a separate email or call.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="report-filters">
        <button onClick={() => setFilter(null)}
          className={`rounded-full border px-3 py-1 text-xs ${!filter ? "border-terracotta bg-terracotta text-white" : "border-border text-charcoal"}`}
          data-testid="report-filter-all">All</button>
        {STATES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} title={STATE_MEANING[s]}
            className={`rounded-full border px-3 py-1 text-xs ${filter === s ? "border-terracotta bg-terracotta text-white" : "border-border text-charcoal"}`}
            data-testid={`report-filter-${s.toLowerCase()}`}>{s}</button>
        ))}
      </div>

      {reports === null && <p className="text-sm text-warmgrey">Loading…</p>}
      {reports?.length === 0 && (
        <div className="rounded-md border border-border bg-white p-6 text-sm text-warmgrey" data-testid="reports-empty">
          <p className="font-medium text-charcoal">{filter ? `No reports in “${filter}”.` : "No reports yet."}</p>
          <p className="mt-2">
            To report a problem or send feedback: open <Link to="/dealer/copilot" className="font-medium text-terracotta hover:underline" data-testid="reports-empty-copilot-link">Zelligio Copilot</Link>,
            use the <span className="font-medium text-charcoal">Report</span> control on the conversation or answer you want to flag, and describe the issue.
            You receive an RPT reference immediately, and the report appears here for tracking.
          </p>
          {filter && <p className="mt-2 text-xs">{STATE_MEANING[filter]}</p>}
        </div>
      )}

      <div className="space-y-3">
        {(reports || []).map((r) => {
          const lu = latestUpdate(r);
          const isOpen = openId === r.id;
          return (
            <div key={r.id} className="rounded-md border border-border bg-white p-4" data-testid="report-card">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono text-xs font-semibold text-charcoal" data-testid="report-ref">{r.reference}</span>
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATE_STYLE[r.status] || ""}`}
                    title={STATE_MEANING[r.status]} data-testid="report-status">{r.status}</span>
                </div>
                <span className="text-xs text-warmgrey whitespace-nowrap" data-testid="report-submitted">Submitted {new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              <p className="mt-2 text-sm font-medium text-charcoal" data-testid="report-summary">{summarize(r.text)}</p>
              <p className="mt-1 text-xs text-warmgrey" data-testid="report-latest-update">
                Latest update: {lu.text} · <span className="whitespace-nowrap">{new Date(lu.at).toLocaleString()}</span>
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-warmgrey">
                <span data-testid="report-origin">From {r.page || "Copilot"}{r.module && r.module !== "copilot" ? ` · ${r.module}` : ""} · {r.mode} mode</span>
                <span>{r.user_name} · {r.user_role}</span>
                {r.conversation?.session_id && (
                  <Link to={`/dealer/copilot?session=${r.conversation.session_id}`}
                    className="font-medium text-terracotta hover:underline" data-testid="report-conversation-link">
                    Open conversation
                  </Link>
                )}
                <button onClick={() => setOpenId(isOpen ? null : r.id)}
                  className="font-medium text-terracotta hover:underline" data-testid="report-history-toggle">
                  {isOpen ? "Hide details" : "View details"}
                </button>
              </div>
              {isOpen && (
                <div className="mt-3 border-t border-border pt-3" data-testid="report-history">
                  <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">What you reported</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-charcoal" data-testid="report-full-text">{r.text}</p>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-warmgrey">History</p>
                  {(r.history || []).map((h, i) => (
                    <p key={i} className="text-[11px] text-warmgrey">
                      {new Date(h.at).toLocaleString()} — {h.actor_name || h.actor}: {h.action}{h.note ? ` (${h.note})` : ""}
                    </p>
                  ))}
                  {r.resolution && (
                    <p className="mt-1 text-[11px] text-green-700" data-testid="report-resolution">Resolved {new Date(r.resolution.at).toLocaleString()}{r.resolution.note ? ` — ${r.resolution.note}` : ""}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
